import uniq from 'lodash-es/uniq'
import { reverse } from 'named-urls'
import type { ComponentProps } from 'react'

import type { CollectionsApi } from '@fourthwall/services/dist/CollectionsApiClient'
import type { Offer } from '@fourthwall/services/dist/models/offer'

import type { StatusTagDropdown } from '../../../components/composed'
import { routing } from '../../../utils/routing'
import type { FormValues } from './types'

export const getCollectionTimeFrame = (
  isTimeLimited: boolean,
  startDate?: FormValues['startDate'],
  endDate?: FormValues['endDate'],
): CollectionsApi.GetCollection.Response['timeFrame'] => {
  if (!isTimeLimited) {
    return { '@type': 'TIME_FRAME_NONE' }
  }

  if (startDate && !endDate) {
    return { '@type': 'TIME_FRAME_FROM', from: startDate.toISOString() }
  }

  if (!startDate && endDate) {
    return { '@type': 'TIME_FRAME_TO', to: endDate.toISOString() }
  }

  if (startDate && endDate) {
    return { '@type': 'TIME_FRAME_RANGE', from: startDate.toISOString(), to: endDate.toISOString() }
  }

  return { '@type': 'TIME_FRAME_NONE' }
}

export const getCreateCollectionPayload = (
  formValues: FormValues,
): CollectionsApi.CreateCollection.RequestBody => {
  const { name, isSoldOut, isTimeLimited, selectedProducts, endDate, startDate } = formValues

  return {
    name,
    description: '',
    available: !isSoldOut,
    offerIds: selectedProducts,
    timeFrame: getCollectionTimeFrame(isTimeLimited, startDate, endDate),
  }
}

export const getUpdateCollectionDetailsPayload = (
  formValues: FormValues,
  shouldUpdateOffersStatus: boolean,
) => {
  const { name, selectedProducts } = formValues

  return {
    newName: name,
    newOfferIds: selectedProducts,
    shouldUpdateOffersStatus,
  }
}

export const getShouldDisplayStatusConfirmation = (
  status: Parameters<ComponentProps<typeof StatusTagDropdown>['onSubmit']>[0]['status'],
  selectedOffers: (Offer.Item | undefined)[],
) => {
  // there are no offers selected
  if (selectedOffers.length === 0) {
    return false
  }

  // some offers are not loaded
  if (selectedOffers.includes(undefined)) {
    return true
  }

  const selectedOffersStatuses = uniq(selectedOffers.map((offer) => offer?.state.status))

  if (
    // all offer statuses are equal
    selectedOffersStatuses.length === 1 &&
    // new status is not schedule
    status !== 'SCHEDULE' &&
    // new status is equal to offer status
    status === selectedOffersStatuses[0]
  ) {
    return false
  }

  // display confirmation by default
  return true
}

export const getProductLink = (productId: string) => {
  return reverse(routing.products.all.product.self, { productId })
}

export const sleep = async (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
